import type { GraphLink, GraphNodeData } from '../indexedDb.helper';

/**
 * Fully qualified name of OpenRewrite's Singleton marker recipe. A recipe
 * that declares this as a precondition is a Singleton: repeat occurrences of
 * the same `(recipe.name, options)` identity collapse to a single canonical
 * vertex with REFERENCE-typed back-edges. Mirrors
 * `RecipeGraphBuilder.SINGLETON_RECIPE_NAME` in moderne-saas.
 */
const SINGLETON_RECIPE_ID = 'org.openrewrite.Singleton';

/**
 * Client port of moderne-saas's `RecipeGraphBuilder` algorithm. Walks the
 * builder's flat graph from `rootId` and produces a canonicalized graph
 * where every Singleton-marked recipe appears exactly once; repeat
 * occurrences are emitted as reference leaves (`isReference: true`,
 * `referencesNodeId` pointing at the canonical).
 *
 * Used to keep the builder's local state in the same shape the backend
 * would produce for the equivalent recipe tree, regardless of which path
 * mutated the state. Eliminates the cross-fragment seam where each fragment
 * fetch is internally collapsed but cross-fragment duplication slips in on
 * merge.
 *
 * The output graph is up to vertex-id renumbering equivalent to
 * `RecipeGraphBuilder.build(descriptor).graph` for the descriptor tree
 * recovered from these nodes/links. The root keeps id 0 because it is the
 * first vertex emitted.
 *
 * Semantics:
 *  - Singleton detection: a node is a Singleton iff one of its precondition
 *    children has `val.id === 'org.openrewrite.Singleton'`. Matches saas's
 *    `hasSingletonPrecondition(descriptor)`.
 *  - Identity: `(node.val.id, sorted options)`. Matches OpenRewrite's
 *    `@EqualsAndHashCode` semantics on recipe instances.
 *  - Walk order: preconditions before recipeList children (matches saas's
 *    `walk` recursion order so REFERENCE-vs-canonical assignment is stable).
 *  - References to a canonical that gets dedup-collapsed are retargeted to
 *    the surviving canonical's new id.
 */
export const normalizeRecipeGraph = (
  nodes: GraphNodeData[],
  links: GraphLink[],
  rootId: number
): { nodes: GraphNodeData[]; links: GraphLink[] } => {
  if (!nodes?.length) {
    return { nodes: [], links: [] };
  }

  const nodeById = new Map<number, GraphNodeData>();
  for (const node of nodes) {
    nodeById.set(node.id, node);
  }

  // Resolve the actual entry point. If the caller's `rootId` is not in the
  // graph, fall back to the node with no incoming links — guards against
  // accidentally emptying a recipe whose root id drifted from the
  // ROOT_NODE_ID convention.
  let effectiveRootId = rootId;
  if (!nodeById.has(effectiveRootId)) {
    const linkTargets = new Set<number>();
    for (const link of links ?? []) {
      linkTargets.add(link.target);
    }
    const detected = nodes.find((n) => !linkTargets.has(n.id));
    if (!detected) {
      // No deterministic root — return input unchanged rather than wipe state.
      return {
        nodes: [...nodes],
        links: [...(links ?? [])]
      };
    }
    effectiveRootId = detected.id;
  }

  const childLinksByParent = new Map<number, GraphLink[]>();
  for (const link of links ?? []) {
    const list = childLinksByParent.get(link.source) ?? [];
    list.push(link);
    childLinksByParent.set(link.source, list);
  }

  const isSingletonByOldId = new Map<number, boolean>();
  for (const node of nodes) {
    const children = childLinksByParent.get(node.id) ?? [];
    const flagged = children.some((link) => {
      const child = nodeById.get(link.target);
      return Boolean(
        child?.isPrecondition && child.val?.id === SINGLETON_RECIPE_ID
      );
    });
    isSingletonByOldId.set(node.id, flagged);
  }

  const outNodes: GraphNodeData[] = [];
  const outLinks: GraphLink[] = [];
  const singletonRegistry = new Map<string, number>();
  const oldIdToNewId = new Map<number, number>();
  let nextId = 0;

  const emitReference = (
    parentNewId: number | undefined,
    canonicalNewId: number,
    source: GraphNodeData,
    edgeIsPrecondition: boolean
  ): void => {
    const refNewId = nextId++;
    outNodes.push({
      ...source,
      id: refNewId,
      isReference: true,
      referencesNodeId: canonicalNewId,
      isPrecondition: edgeIsPrecondition
    });
    if (parentNewId !== undefined) {
      outLinks.push({ source: parentNewId, target: refNewId });
    }
  };

  const walk = (
    oldId: number,
    parentNewId: number | undefined,
    edgeIsPrecondition: boolean
  ): void => {
    const node = nodeById.get(oldId);
    if (!node) {
      return;
    }

    // Existing reference nodes pass through. Their `referencesNodeId` is
    // patched in the second pass once every canonical's new id is known.
    if (node.isReference && node.referencesNodeId !== undefined) {
      const canonicalNewId = oldIdToNewId.get(node.referencesNodeId);
      if (canonicalNewId !== undefined) {
        emitReference(parentNewId, canonicalNewId, node, edgeIsPrecondition);
        return;
      }
      const refNewId = nextId++;
      outNodes.push({
        ...node,
        id: refNewId,
        isReference: true,
        referencesNodeId: node.referencesNodeId,
        isPrecondition: edgeIsPrecondition
      });
      if (parentNewId !== undefined) {
        outLinks.push({ source: parentNewId, target: refNewId });
      }
      return;
    }

    const flagged = isSingletonByOldId.get(oldId) ?? false;
    const key = flagged ? singletonIdentityKey(node) : null;
    if (key !== null && singletonRegistry.has(key)) {
      const canonicalNewId = singletonRegistry.get(key) as number;
      // Remap the deduped node's old id so within-fragment references that
      // pointed at it now resolve to the surviving canonical.
      oldIdToNewId.set(oldId, canonicalNewId);
      emitReference(parentNewId, canonicalNewId, node, edgeIsPrecondition);
      return;
    }

    const newId = nextId++;
    oldIdToNewId.set(oldId, newId);
    outNodes.push({
      ...node,
      id: newId,
      isPrecondition: edgeIsPrecondition,
      isReference: false,
      referencesNodeId: undefined
    });
    if (parentNewId !== undefined) {
      outLinks.push({ source: parentNewId, target: newId });
    }
    if (key !== null) {
      singletonRegistry.set(key, newId);
    }

    const children = childLinksByParent.get(oldId) ?? [];
    const preconditions: GraphLink[] = [];
    const recipes: GraphLink[] = [];
    for (const link of children) {
      const child = nodeById.get(link.target);
      if (child?.isPrecondition) {
        preconditions.push(link);
      } else {
        recipes.push(link);
      }
    }
    for (const link of preconditions) {
      walk(link.target, newId, true);
    }
    for (const link of recipes) {
      walk(link.target, newId, false);
    }
  };

  walk(effectiveRootId, undefined, false);

  // Defensive: if the walk produced no output but the input was non-empty,
  // something is structurally wrong with the graph (cycle, orphaned root,
  // unexpected shape). Returning the original is strictly safer than
  // wiping the user's recipe.
  if (outNodes.length === 0) {
    return {
      nodes: [...nodes],
      links: [...(links ?? [])]
    };
  }

  // Patch references whose canonical hadn't been assigned a new id yet at
  // the time the reference was emitted (forward references within a single
  // fragment that the walker hit before the canonical).
  for (const node of outNodes) {
    if (node.isReference && node.referencesNodeId !== undefined) {
      const remapped = oldIdToNewId.get(node.referencesNodeId);
      if (remapped !== undefined) {
        node.referencesNodeId = remapped;
      }
    }
  }

  return { nodes: outNodes, links: outLinks };
};

/**
 * Stable identity key for Singleton dedup. Mirrors moderne-saas's
 * `SingletonKey(recipeName, options)` — different option values keep recipes
 * distinct (matches OpenRewrite's `@EqualsAndHashCode` equality).
 */
const singletonIdentityKey = (node: GraphNodeData): string => {
  const recipeName = node.val?.id ?? '';
  const options = (node.val?.options ?? [])
    .map((option) => [option?.name ?? '', option?.value ?? null])
    .sort(([a], [b]) => String(a).localeCompare(String(b)));
  return `${recipeName}::${JSON.stringify(options)}`;
};
